import { createClient } from '@supabase/supabase-js';
import type { HyperSession } from '../types/session';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

const supabase = createClient(supabaseUrl, supabaseAnonKey);

/**
 * Infer MIME type from file extension
 */
const inferMimeTypeFromExtension = (extension?: string): string => {
  if (!extension) return 'application/octet-stream';
  
  const ext = extension.toLowerCase();
  
  // Image types
  const imageTypes: Record<string, string> = {
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'png': 'image/png',
    'gif': 'image/gif',
    'webp': 'image/webp',
    'svg': 'image/svg+xml',
    'bmp': 'image/bmp',
    'tiff': 'image/tiff',
    'ico': 'image/x-icon'
  };
  
  // Video types
  const videoTypes: Record<string, string> = {
    'mp4': 'video/mp4',
    'avi': 'video/x-msvideo',
    'mov': 'video/quicktime',
    'wmv': 'video/x-ms-wmv',
    'flv': 'video/x-flv',
    'webm': 'video/webm',
    'mkv': 'video/x-matroska',
    'm4v': 'video/x-m4v',
    '3gp': 'video/3gpp'
  };
  
  // Audio types
  const audioTypes: Record<string, string> = {
    'mp3': 'audio/mpeg',
    'wav': 'audio/wav',
    'flac': 'audio/flac',
    'aac': 'audio/aac',
    'ogg': 'audio/ogg',
    'm4a': 'audio/mp4',
    'wma': 'audio/x-ms-wma',
    'opus': 'audio/opus'
  };
  
  return imageTypes[ext] || videoTypes[ext] || audioTypes[ext] || 'application/octet-stream';
};

/**
 * Ensure user exists in Supabase users table
 */
export const ensureUserExists = async (session: HyperSession): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log('👤 [USER] Ensuring user exists in database');
    console.log('🔍 [USER] User ID:', session.hy_user_id);
    
    // Check if user already exists
    const { data: existingUser, error: fetchError } = await supabase
      .from('users')
      .select('hy_user_id')
      .eq('hy_user_id', session.hy_user_id)
      .single();

    if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 = no rows returned
      console.error('❌ [USER] Error checking user existence:', fetchError);
      throw fetchError;
    }

    if (existingUser) {
      console.log('✅ [USER] User already exists in database');
      return { success: true };
    }

    // User doesn't exist, create them
    console.log('➕ [USER] Creating new user in database');
    const { error: insertError } = await supabase
      .from('users')
      .insert({
        hy_user_id: session.hy_user_id,
        hy_name: session.hy_name,
        hy_country: session.hy_country,
        hy_country_code: session.hy_country_code
      });

    if (insertError) {
      console.error('❌ [USER] Error creating user:', insertError);
      throw insertError;
    }

    console.log('🎉 [USER] User created successfully');
    return { success: true };

  } catch (error) {
    console.error('💥 [USER] Fatal error ensuring user exists:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to ensure user exists'
    };
  }
};

/**
 * Upload file to Supabase storage and create asset record
 */
export const uploadFile = async (
  file: File,
  userId: string,
  bucket: 'uploaded' | 'generated' = 'uploaded',
  isAIGenerated: boolean = false,
  appId: string = '',
  metadata?: {
    fal_request_id?: string;
    generation_prompt?: string;
    generation_params?: any;
    model?: string;
  }
): Promise<{ success: boolean; assetId?: string; publicUrl?: string; error?: string }> => {
  try {
    // First, ensure user exists in database (we need the session for this)
    // Note: This requires the session to be passed to this function
    // For now, we'll assume the user exists. In a real implementation,
    // this should be called from a higher level with the session.
    console.log('⚠️ [USER] Note: User existence not checked in uploadFile - should be done at app level');
    
    console.log('🔄 [UPLOAD] Starting upload process');
    console.log('📋 [UPLOAD] User ID:', userId);
    console.log('📁 [UPLOAD] File:', file.name, file.size, 'bytes');
    console.log('📋 [UPLOAD] Original MIME type:', file.type);
    console.log('📝 [UPLOAD] Original filename:', file.name);
    console.log('🪣 [UPLOAD] Bucket:', bucket);
    console.log('🆔 [UPLOAD] App ID provided:', appId);
    
    // Validate and fix MIME type if needed
    const fileExt = file.name.split('.').pop();
    let finalFile = file;
    
    if (!file.type || file.type === 'application/octet-stream') {
      const inferredMimeType = inferMimeTypeFromExtension(fileExt);
      console.log('🎯 [UPLOAD] Inferred MIME type:', inferredMimeType);
      
      // Create new file with correct MIME type
      finalFile = new File([file], file.name, {
        type: inferredMimeType,
        lastModified: file.lastModified
      });
    }
    
    console.log('📋 [UPLOAD] Final MIME type:', finalFile.type);
    
    // Create unique file path
    const fileName = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
    const filePath = `${userId}/${fileName}`;
    
    console.log('📂 [UPLOAD] File path:', filePath);

    // Determine media type from file extension
    const getMediaType = (extension?: string): 'image' | 'video' | 'audio' => {
      const ext = extension?.toLowerCase() || '';
      
      if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'tiff', 'ico'].includes(ext)) {
        return 'image';
      }
      if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv', 'm4v', '3gp'].includes(ext)) {
        return 'video';
      }
      if (['mp3', 'wav', 'flac', 'aac', 'ogg', 'm4a', 'wma', 'opus'].includes(ext)) {
        return 'audio';
      }
      return 'image'; // default
    };

    console.log('🎭 [UPLOAD] Media type detected:', getMediaType(fileExt));
    
    // Upload file to storage
    console.log('☁️ [UPLOAD] Starting storage upload...');
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from(bucket)
      .upload(filePath, finalFile, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) {
      console.error('❌ [UPLOAD] Storage upload failed:', uploadError);
      throw uploadError;
    }

    console.log('✅ [UPLOAD] Storage upload successful:', uploadData);
    
    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from(bucket)
      .getPublicUrl(filePath);

    console.log('🔗 [UPLOAD] Public URL generated:', publicUrl);
    
    // Insert asset record directly
    console.log('💾 [UPLOAD] Saving asset record to database...');
    const assetRecord = {
      hy_user_id: userId,
      public_url: publicUrl,
      isaigenerated: isAIGenerated,
      filename: finalFile.name,
      file_size: finalFile.size,
      media_type: getMediaType(fileExt),
      file_extension: fileExt?.toLowerCase() || null,
      fal_request_id: metadata?.fal_request_id || null,
      generation_prompt: metadata?.generation_prompt || null,
      generation_params: metadata?.generation_params || null,
      model: metadata?.model || null,
      app_id: appId || ''
    };
    
    console.log('📊 [UPLOAD] Asset record with app_id:', { ...assetRecord, app_id: assetRecord.app_id });
    
    const { data: assetData, error: assetError } = await supabase
      .from('assets')
      .insert(assetRecord)
      .select('id')
      .single();

    if (assetError) {
      console.error('❌ [UPLOAD] Database insert failed:', assetError);
      // Clean up uploaded file if asset record creation fails
      console.log('🗑️ [UPLOAD] Cleaning up uploaded file...');
      const { error: removeError } = await supabase.storage.from(bucket).remove([filePath]);
      if (removeError) {
        console.error('⚠️ [UPLOAD] Failed to clean up file:', removeError);
      }
      throw assetError;
    }

    console.log('🎉 [UPLOAD] Upload completed successfully!');
    console.log('🆔 [UPLOAD] Asset ID:', assetData.id);
    
    return {
      success: true,
      assetId: assetData.id,
      publicUrl
    };

  } catch (error) {
    console.error('💥 [UPLOAD] Fatal upload error:', error);
    console.error('🔍 [UPLOAD] Error details:', {
      name: error instanceof Error ? error.name : 'Unknown',
      message: error instanceof Error ? error.message : 'Unknown error',
      stack: error instanceof Error ? error.stack : 'No stack trace'
    });
    
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Upload failed'
    };
  }
};

/**
 * Upload generated image from URL (e.g., FAL AI output)
 */
export const uploadGeneratedMediaFromUrl = async (
  mediaUrl: string,
  userId: string,
  appId: string,
  originalFilename?: string,
  fileSize?: number,
  metadata?: {
    generation_prompt?: string;
    generation_params?: any;
  },
  assetIdToUpdate?: string
): Promise<{ success: boolean; assetId?: string; publicUrl?: string; error?: string }> => {
  return uploadGeneratedMediaFromUrlWithUpdate(
    mediaUrl,
    userId,
    appId,
    originalFilename,
    fileSize,
    metadata,
    assetIdToUpdate
  );
};

/**
 * Upload generated media from URL with option to update existing asset record
 */
export const uploadGeneratedMediaFromUrlWithUpdate = async (
  mediaUrl: string,
  userId: string,
  appId: string,
  originalFilename?: string,
  fileSize?: number,
  metadata?: {
    generation_prompt?: string;
    generation_params?: any;
  },
  assetIdToUpdate?: string
): Promise<{ success: boolean; assetId?: string; publicUrl?: string; error?: string }> => {
  try {
    console.log('📁 [UPLOAD-MEDIA] Starting upload process');
    console.log('🔄 [UPLOAD-MEDIA] Mode:', assetIdToUpdate ? 'UPDATE' : 'CREATE');
    console.log('🔗 [UPLOAD-MEDIA] Media URL:', mediaUrl);
    
    // Validate media URL
    if (!mediaUrl || typeof mediaUrl !== 'string' || mediaUrl.trim() === '') {
      throw new Error('Invalid media URL: URL is empty or not a string');
    }
    
    if (!mediaUrl.startsWith('http://') && !mediaUrl.startsWith('https://')) {
      throw new Error('Invalid media URL: URL must start with http:// or https://');
    }
    
    // Fetch media directly from URL (client-side)
    console.log('🌐 [UPLOAD-MEDIA] Fetching media from URL...');
    let response: Response;
    let blob: Blob;
    
    try {
      // Direct fetch from FAL URL
      response = await fetch(mediaUrl, {
        method: 'GET',
        headers: {
          'Accept': '*/*',
          'Cache-Control': 'no-cache'
        },
        mode: 'cors'
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      blob = await response.blob();
      console.log('✅ [UPLOAD-MEDIA] Direct fetch successful');
      console.log('📊 [UPLOAD-MEDIA] Blob size:', blob.size, 'bytes');
      console.log('📋 [UPLOAD-MEDIA] Content type:', blob.type);
    } catch (fetchError) {
      console.error('❌ [UPLOAD-MEDIA] Direct fetch failed:', fetchError);
      
      // Try alternative approach with no-cors mode
      try {
        console.log('🔄 [UPLOAD-MEDIA] Trying no-cors mode...');
        response = await fetch(mediaUrl, {
          method: 'GET',
          mode: 'no-cors'
        });
        
        blob = await response.blob();
        console.log('✅ [UPLOAD-MEDIA] No-cors fetch successful');
        console.log('📊 [UPLOAD-MEDIA] Blob size:', blob.size, 'bytes');
      } catch (noCorsError) {
        console.error('❌ [UPLOAD-MEDIA] No-cors fetch also failed:', noCorsError);
        
        // Final fallback: Create a reference-only asset record without downloading
        console.log('🔄 [UPLOAD-MEDIA] Creating reference-only asset record...');
        return await createReferenceOnlyAsset(
          mediaUrl,
          userId,
          appId,
          originalFilename,
          fileSize,
          metadata,
          assetIdToUpdate
        );
      }
    }
    
    // Create file object
    const blobType = blob.type;
    const fileExt = originalFilename ? originalFilename.split('.').pop() : 'jpg';
    
    // Determine proper MIME type
    let finalContentType = blobType;
    
    // If we have a generic or missing MIME type, infer from extension
    if (!finalContentType || finalContentType === 'application/octet-stream' || finalContentType === '') {
      finalContentType = inferMimeTypeFromExtension(fileExt);
      console.log('🎯 [UPLOAD-MEDIA] Inferred MIME type from extension:', finalContentType);
    }
    
    console.log('📋 [UPLOAD-MEDIA] Final content type:', finalContentType);
    
    const generatedFileName = originalFilename || `ai-generated-${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
    
    // Create file object with proper metadata
    const file = new File([blob], generatedFileName, { 
      type: finalContentType,
      lastModified: Date.now()
    });
    
    // Override file size if provided from FAL
    if (fileSize) {
      Object.defineProperty(file, 'size', {
        value: fileSize,
        writable: false
      });
    }

    if (assetIdToUpdate) {
      // Update existing asset record
      console.log('🔄 [UPLOAD-MEDIA] Updating existing asset:', assetIdToUpdate);
      
      // Create unique file path for the generated media
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
      const filePath = `${userId}/${fileName}`;
      
      const { error: uploadError } = await supabase.storage
        .from('generated')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        throw uploadError;
      }

      // Get public URL
      console.log('✅ [UPLOAD] Storage upload successful');
      const { data: { publicUrl } } = supabase.storage
        .from('generated')
        .getPublicUrl(filePath);

      // Determine media type from file extension
      const getMediaType = (extension?: string): 'image' | 'video' | 'audio' => {
        const ext = extension?.toLowerCase() || '';
        
        if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'tiff', 'ico'].includes(ext)) {
          return 'image';
        }
        if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv', 'm4v', '3gp'].includes(ext)) {
          return 'video';
        }
        if (['mp3', 'wav', 'flac', 'aac', 'ogg', 'm4a', 'wma', 'opus'].includes(ext)) {
          return 'audio';
        }
        return 'image'; // default
      };

      // Update existing asset record
      const { error: updateError } = await supabase
        .from('assets')
        .update({
          public_url: publicUrl,
          status: 'completed',
          filename: file.name,
          file_size: file.size,
          media_type: getMediaType(fileExt),
          file_extension: fileExt?.toLowerCase() || null,
          generation_prompt: metadata?.generation_prompt || null,
          generation_params: metadata?.generation_params || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', assetIdToUpdate);

      if (updateError) {
        // Clean up uploaded file if update fails
        await supabase.storage.from('generated').remove([filePath]);
        throw updateError;
      }

      console.log('✅ [UPLOAD-MEDIA] Existing asset updated successfully');
      return {
        success: true,
        assetId: assetIdToUpdate,
        publicUrl
      };
    } else {
      // Create new asset record using existing function
      console.log('➕ [UPLOAD-MEDIA] Creating new asset record');
      return await uploadFile(file, userId, 'generated', true, appId, metadata);
    }

  } catch (error) {
    console.error('💥 [UPLOAD-MEDIA] Upload error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Media upload failed'
    };
  }
};

/**
 * Create reference-only asset record when media can't be downloaded
 */
const createReferenceOnlyAsset = async (
  mediaUrl: string,
  userId: string,
  appId: string,
  originalFilename?: string,
  fileSize?: number,
  metadata?: {
    generation_prompt?: string;
    generation_params?: any;
  },
  assetIdToUpdate?: string
): Promise<{ success: boolean; assetId?: string; publicUrl?: string; error?: string }> => {
  try {
    console.log('📋 [REFERENCE-ASSET] Creating reference-only asset record');
    console.log('🔗 [REFERENCE-ASSET] Direct FAL URL will be used:', mediaUrl);
    
    // Determine media type from URL or filename
    const getMediaTypeFromUrl = (url: string): 'image' | 'video' | 'audio' => {
      const urlLower = url.toLowerCase();
      if (urlLower.includes('.mp4') || urlLower.includes('.avi') || urlLower.includes('.mov') || urlLower.includes('.webm')) {
        return 'video';
      }
      if (urlLower.includes('.mp3') || urlLower.includes('.wav') || urlLower.includes('.flac') || urlLower.includes('.aac')) {
        return 'audio';
      }
      return 'image';
    };
    
    const mediaType = getMediaTypeFromUrl(mediaUrl);
    const fileExt = originalFilename?.split('.').pop()?.toLowerCase() || 
                   (mediaType === 'video' ? 'mp4' : mediaType === 'audio' ? 'mp3' : 'jpg');
    
    if (assetIdToUpdate) {
      // Update existing asset record with direct FAL URL
      const { error: updateError } = await supabase
        .from('assets')
        .update({
          public_url: mediaUrl, // Use FAL URL directly
          status: 'completed',
          filename: originalFilename || `generated.${fileExt}`,
          file_size: fileSize || null,
          media_type: mediaType,
          file_extension: fileExt,
          generation_prompt: metadata?.generation_prompt || null,
          generation_params: metadata?.generation_params || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', assetIdToUpdate);

      if (updateError) {
        throw updateError;
      }

      console.log('✅ [REFERENCE-ASSET] Existing asset updated with FAL URL');
      return {
        success: true,
        assetId: assetIdToUpdate,
        publicUrl: mediaUrl
      };
    } else {
      // Create new asset record with direct FAL URL
      const assetRecord = {
        hy_user_id: userId,
        public_url: mediaUrl, // Use FAL URL directly
        isaigenerated: true,
        filename: originalFilename || `generated-${Date.now()}.${fileExt}`,
        file_size: fileSize || null,
        media_type: mediaType,
        file_extension: fileExt,
        generation_prompt: metadata?.generation_prompt || null,
        generation_params: metadata?.generation_params || null,
        status: 'completed',
        app_id: appId
      };

      const { data: assetData, error: assetError } = await supabase
        .from('assets')
        .insert(assetRecord)
        .select('id')
        .single();

      if (assetError) {
        throw assetError;
      }

      console.log('✅ [REFERENCE-ASSET] New asset created with FAL URL');
      console.log('🆔 [REFERENCE-ASSET] Asset ID:', assetData.id);
      
      return {
        success: true,
        assetId: assetData.id,
        publicUrl: mediaUrl
      };
    }

  } catch (error) {
    console.error('💥 [REFERENCE-ASSET] Error creating reference asset:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create reference asset'
    };
  }
};

// Keep backward compatibility
export const uploadGeneratedImageFromUrl = uploadGeneratedMediaFromUrl;
/**
 * Get user assets from database
 */
export const getUserAssets = async (
  userId: string,
  isAIGenerated?: boolean,
  limit: number = 50,
  offset: number = 0
) => {
  try {
    const isMockUser = userId === 'FTYVyc9tpPXR6QodX4joUSOCHDN2';
    console.log('🗃️ [DB] getUserAssets called with:', {
      userId,
      isMockUser,
      isAIGenerated,
      limit,
      offset
    });
    
    if (!userId || userId.trim() === '') {
      console.error('❌ [DB] Invalid userId provided:', userId);
      return {
        success: false,
        error: 'Invalid user ID provided',
        assets: []
      };
    }
    
    // For real sessions, validate hy_user_id format
    if (!isMockUser && userId.length < 10) {
      console.error('❌ [DB] Invalid hy_user_id format for real session:', userId);
      return {
        success: false,
        error: 'Invalid user ID format - must be valid Hyper user ID',
        assets: []
      };
    }
    
    // Critical security check: Ensure we're fetching for the exact user
    console.log('🔐 [DB] SECURITY CHECK: Fetching assets for user:', userId);
    console.log('🔐 [DB] Query will use filter: hy_user_id = ?', userId);
    
    let query = supabase
      .from('assets')
      .select(`
        *,
        file_size,
        media_type,
        file_extension,
        fal_request_id,
        generation_prompt,
        generation_params
      `)
      .eq('hy_user_id', userId)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    console.log(`📋 [DB] Query filter for ${isMockUser ? 'MOCK' : 'REAL'} user: hy_user_id =`, userId);
    
    if (isAIGenerated !== undefined) {
      query = query.eq('isaigenerated', isAIGenerated);
      console.log('📋 [DB] Additional filter: isaigenerated =', isAIGenerated);
    }

    const { data, error } = await query;

    console.log('📥 [DB] Supabase response:', {
      success: !error,
      dataLength: data?.length || 0,
      error: error?.message
    });
    
    if (error) {
      console.error('❌ [DB] Supabase query failed:', error);
      throw error;
    }

    console.log(`✅ [DB] Successfully fetched ${data?.length || 0} assets for user ${userId}`);
    
    // Debug: Log first few assets to verify correct user filtering
    if (data && data.length > 0) {
      console.log('🔍 [DB] First asset sample:', {
        id: data[0].id,
        hy_user_id: data[0].hy_user_id,
        filename: data[0].filename,
        created_at: data[0].created_at
      });
      
      // Verify all assets belong to the correct user
      const wrongUserAssets = data.filter(asset => asset.hy_user_id !== userId);
      if (wrongUserAssets.length > 0) {
        console.error('🚨 [DB] SECURITY ALERT: Found assets for different users!', wrongUserAssets.map(a => ({
          id: a.id,
          expected_user: userId,
          actual_user: a.hy_user_id
        })));
      } else {
        console.log('🔐 [DB] Security check passed: All assets belong to user', userId);
      }
    }
    // Debug: Log first few assets to verify correct user filtering
    if (data && data.length > 0) {
      console.log('🔍 [DB] First asset sample:', {
        id: data[0].id,
        hy_user_id: data[0].hy_user_id,
        filename: data[0].filename,
        created_at: data[0].created_at
      });
      
      // Verify all assets belong to the correct user
      const wrongUserAssets = data.filter(asset => asset.hy_user_id !== userId);
      if (wrongUserAssets.length > 0) {
        console.error('🚨 [DB] SECURITY ALERT: Found assets for different users!', wrongUserAssets.map(a => ({
          id: a.id,
          expected_user: userId,
          actual_user: a.hy_user_id
        })));
      } else {
        console.log('🔐 [DB] Security check passed: All assets belong to user', userId);
      }
    }
    
    return {
      success: true,
      assets: data
    };

  } catch (error) {
    console.error('💥 [DB] Get assets error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get assets',
      assets: []
    };
  }
};

/**
 * Delete asset and file from storage
 */
export const deleteAsset = async (
  assetId: string,
  userId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Get asset details first
    const { data: asset, error: fetchError } = await supabase
      .from('assets')
      .select('*')
      .eq('id', assetId)
      .eq('hy_user_id', userId)
      .single();

    if (fetchError || !asset) {
      throw new Error('Asset not found or access denied');
    }

    // Extract bucket and path from public URL
    const url = new URL(asset.public_url);
    const urlParts = url.pathname.split('/');
    const bucket = urlParts[4]; // /storage/v1/object/public/{bucket}/...
    const filePath = urlParts.slice(5).join('/'); // user_id/filename

    // Delete from storage
    const { error: storageError } = await supabase.storage
      .from(bucket)
      .remove([filePath]);

    if (storageError) {
      console.warn('Storage deletion failed:', storageError);
      // Continue with database deletion even if storage fails
    }

    // Delete from database
    const { error: dbError } = await supabase
      .from('assets')
      .delete()
      .eq('id', assetId)
      .eq('hy_user_id', userId);

    if (dbError) {
      throw dbError;
    }

    return { success: true };

  } catch (error) {
    console.error('Delete asset error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Delete failed'
    };
  }
};

/**
 * Get public URL for a file in storage
 */
export const getPublicUrl = (bucket: string, filePath: string): string => {
  const { data } = supabase.storage.from(bucket).getPublicUrl(filePath);
  return data.publicUrl;
};

/**
 * Get asset by public URL from database
 */
export const getAssetByPublicUrl = async (
  publicUrl: string,
  userId: string
): Promise<{ success: boolean; asset?: any; error?: string }> => {
  try {
    // Check for empty or invalid public URL
    if (!publicUrl || publicUrl.trim() === '') {
      return {
        success: false,
        error: 'Asset not found'
      };
    }

    const { data, error } = await supabase
      .from('assets')
      .select(`
        *,
        file_size,
        media_type,
        file_extension,
        fal_request_id,
        generation_prompt,
        generation_params
      `)
      .eq('public_url', publicUrl)
      .eq('hy_user_id', userId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return {
          success: false,
          error: 'Asset not found'
        };
      }
      throw error;
    }

    return {
      success: true,
      asset: data
    };

  } catch (error) {
    console.error('Get asset by URL error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get asset'
    };
  }
};

/**
 * Get all pro forms from database
 */
export const getProForms = async (
  userId?: string
): Promise<{ success: boolean; forms?: any[]; error?: string }> => {
  try {
    let query = supabase
      .from('pro_forms')
      .select('*')
      .order('created_at', { ascending: false });

    if (userId) {
      query = query.eq('hy_user_id', userId);
    }

    const { data, error } = await query;

    if (error) throw error;

    return {
      success: true,
      forms: data
    };
  } catch (error) {
    console.error('Get pro forms error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get pro forms',
      forms: []
    };
  }
};

/**
 * Save or update pro form configuration in the pro_forms table
 */
export const saveProFormConfiguration = async ({
  formConfig,
  sections,
  isUpdate = false,
  existingFormId,
  currentUserId
}: {
  formConfig: any;
  sections: any[];
  isUpdate?: boolean;
  existingFormId?: string;
  currentUserId?: string;
}): Promise<{ success: boolean; formId?: string; error?: string }> => {
  try {
    console.log('💾 [PRO-FORM-SAVE] Starting save operation');
    
    // Convert form sections to input_schema format
    const inputSchema = sections.flatMap(section =>
      section.fields.map((field: any) => ({
        key: field.variable_name || field.label.toLowerCase().replace(/[^a-z0-9]/g, '_'),
        label: field.label,
        type: field.type,
        required: field.required || false,
        placeholder: field.placeholder || '',
        description: field.description || '',
        advanced: field.advanced || false,
        defaultValue: field.defaultValue,
        hidden: field.hidden || false,
        min: field.min,
        max: field.max,
        step: field.step,
        options: field.options || [],
        multiple: field.multiple,
        accept: field.accept,
        imageOptions: field.imageOptions || [],
        allowCustomValue: field.allowCustomValue || false,
        customValueLabel: field.customValueLabel || '',
        customButtonLabel: field.customButtonLabel || '',
        customInputPlaceholder: field.customInputPlaceholder || '',
        enablePromptEnhancement: field.enablePromptEnhancement || false,
        systemInstructions: field.systemInstructions || undefined,
        promptBuilderConfig: field.promptBuilderConfig ? {
          template: field.promptBuilderConfig.template || '',
          sub_fields: (field.promptBuilderConfig.sub_fields || []).map((subField: any) => ({
            id: subField.id,
            key: subField.key || subField.variable_name,
            type: subField.type,
            label: subField.label,
            variable_name: subField.variable_name,
            required: subField.required || false,
            placeholder: subField.placeholder || '',
            description: subField.description || '',
            defaultValue: subField.defaultValue,
            options: subField.options || [],
            min: subField.min,
            max: subField.max,
            step: subField.step
          }))
        } : undefined
      }))
    );

    // Prepare pro form data for database
    const proFormData = {
      route_path: formConfig.routePath.startsWith('/') ? formConfig.routePath : `/${formConfig.routePath}`,
      form_name: formConfig.formName || 'Untitled Pro Form',
      form_description: formConfig.formDescription || '',
      input_schema: inputSchema,
      request_type: formConfig.requestType || '',
      app_id: formConfig.appId || '',
      credit_cost: formConfig.creditCost || 10,
      example_media_url: formConfig.exampleMediaUrl || null,
      example_media_type: formConfig.exampleMediaType || null,
      hy_user_id: currentUserId || null,
    };

    let result;
    if (isUpdate && existingFormId) {
      result = await supabase
        .from('pro_forms')
        .update(proFormData)
        .eq('id', existingFormId)
        .select('id, route_path')
        .single();
    } else {
      result = await supabase
        .from('pro_forms')
        .insert(proFormData)
        .select('id, route_path')
        .single();
    }

    const { data, error } = result;

    if (error) {
      if (error.code === '23505') {
        return {
          success: false,
          error: `Route path "${formConfig.routePath}" already exists. Please use a different route path.`
        };
      }
      throw error;
    }

    return {
      success: true,
      formId: data.id
    };

  } catch (error) {
    console.error('💥 [PRO-FORM-SAVE] Save error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to save pro form configuration'
    };
  }
};

/**
 * Load existing pro form configuration for editing
 */
export const loadProFormConfiguration = async (formId: string): Promise<{
  success: boolean;
  data?: any;
  error?: string;
}> => {
  try {
    const { data, error } = await supabase
      .from('pro_forms')
      .select('*')
      .eq('id', formId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return { success: false, error: 'Pro form not found' };
      }
      throw error;
    }

    return {
      success: true,
      data: data
    };

  } catch (error) {
    console.error('Error loading pro form configuration:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to load pro form configuration'
    };
  }
};

export { supabase };

/**
 * Create asset placeholder in database for AI generation tracking
 */
export const createAssetPlaceholder = async (
  userId: string,
  falRequestId: string,
  falPollingNodeName: string,
  modelConfig: any,
  formData: Record<string, any>,
  appId: string
): Promise<string | null> => {
  try {
    console.log('🔧 [ASSET-PLACEHOLDER] Creating asset placeholder with app_id:', appId);
    console.log('👤 [ASSET-PLACEHOLDER] User ID:', userId);
    console.log('🆔 [ASSET-PLACEHOLDER] FAL Request ID:', falRequestId);
    
    // Clean the fal_endpoint and create full request path
    let cleanEndpoint = modelConfig.fal_endpoint;
    if (cleanEndpoint.startsWith('https://queue.fal.run/')) {
      cleanEndpoint = cleanEndpoint.replace('https://queue.fal.run/', '');
    }
    
    console.log('🔧 [ASSET] Original endpoint:', modelConfig.fal_endpoint);

    const assetRecord = {
      hy_user_id: userId,
      public_url: '', // Will be updated when generation completes
      isaigenerated: true,
      model: modelConfig.fal_endpoint, // Store full FAL endpoint for polling
      filename: `${modelConfig.name || 'generated'}-${Date.now()}.${modelConfig.output_mapping?.file_extension || 'jpg'}`,
      media_type: modelConfig.output_type,
      file_extension: modelConfig.output_mapping?.file_extension || 'jpg',
      fal_request_id: falRequestId,
      fal_polling_node_name: falPollingNodeName,
      generation_prompt: formData.prompt || JSON.stringify(formData),
      generation_params: {
        ...formData,
        fal_endpoint: modelConfig.fal_endpoint, // Preserve the full endpoint as reference
        model_config: {
          name: modelConfig.name,
          output_type: modelConfig.output_type,
          credit_cost: modelConfig.credit_cost
        }
      },
      status: 'processing',
      app_id: appId
    };

    console.log('💾 [ASSET-PLACEHOLDER] Asset record with explicit app_id:', {
      hy_user_id: assetRecord.hy_user_id,
      app_id: assetRecord.app_id,
      model: assetRecord.model,
      status: assetRecord.status
    });

    const { data, error } = await supabase
      .from('assets')
      .insert(assetRecord)
      .select('id')
      .single();

    if (error) throw error;

    console.log('✅ [ASSET-PLACEHOLDER] Placeholder created successfully:');
    console.log('  - Asset ID:', data.id);
    console.log('  - App ID saved:', appId);
    console.log('  - User ID:', userId);
    return data.id;
  } catch (error) {
    console.error('❌ [ASSET-PLACEHOLDER] Failed to create placeholder:', error);
    return null;
  }
};


/**
 * Create a new pro request in the 'requests' table
 */
export const createProRequest = async (
  userId: string,
  requestType: string,
  prompt: string,
  inputMediaUrl: string | null,
  actionDetails?: Record<string, any>
): Promise<{ success: boolean; requestId?: string; error?: string }> => {
  try {
    console.log('📝 [PRO-REQUEST] Creating new pro request');
    console.log('👤 [PRO-REQUEST] User ID:', userId);
    console.log('📋 [PRO-REQUEST] Request Type:', requestType);
    console.log('💬 [PRO-REQUEST] Prompt:', prompt);
    console.log('📁 [PRO-REQUEST] Input media URL:', inputMediaUrl);

    const { data, error } = await supabase
      .from('requests')
      .insert({
        hy_user_id: userId,
        request_type: requestType,
        prompt: prompt,
        input_media_url: inputMediaUrl,
        action_details: actionDetails || null,
        status: 'pending' // Initial status
      })
      .select('id')
      .single();

    if (error) throw error;

    console.log('🎉 [PRO-REQUEST] Request created successfully with ID:', data.id);
    // Placeholder for notification API call
    console.log('🔔 [PRO-REQUEST] Notification API placeholder: Call notification service here for request ID:', data.id);
    return { success: true, requestId: data.id };
  } catch (error) {
    console.error('💥 [PRO-REQUEST] Error creating request:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Failed to create pro request' };
  }
};

/**
 * Update asset status in database
 */
export const updateAssetStatus = async (
  assetId: string,
  status: string,
  error?: string
): Promise<void> => {
  try {
    const updateData: any = { status };
    if (error) {
      updateData.generation_error = error;
    }

    const { error: updateError } = await supabase
      .from('assets')
      .update(updateData)
      .eq('id', assetId);

    if (updateError) throw updateError;
    
    console.log('✅ [ASSET] Status updated to:', status);
  } catch (error) {
    console.error('❌ [ASSET] Failed to update status:', error);
  }
};